import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { apiFetch } from '../services/api';
import { useSocketStore, type SocketHandler } from './socket';
import { useAuthStore } from './auth';
import { isDeviceRow } from '../utils/userDirectory';

/**
 * A row of the people list (`GET /people`).
 *
 * `role` / `enabled` / `cloudUserId` are here because the route answers the SAME
 * row shape as `/users` — the hub builds it as `getAllUsers()` + presence
 * (`otto/src/server/routes/people.routes.ts`) — and those three are what
 * `utils/userDirectory.ts` reads to tell a person from a machine.
 */
export interface User {
  userId: string;
  /** The derived, invisible handle (hub card `t_a8eb402a`) — never a name.
   *  Render a person with `personName()` from `utils/personName.ts`. */
  username: string;
  fullName: string | null;
  email: string | null;
  avatarImageId: string | null;
  /** A machine row's markers, straight off the hub's `users` table. */
  role?: string | null;
  enabled?: boolean | null;
  cloudUserId?: string | null;
  status?: string;
  customMessage: string | null;
  lastActiveAt: Date | null;
  presenceStatus?: {
    isPresent: boolean;
    zoneName: string | null;
    detectionMethod: string | null;
  };
}

export interface Conversation {
  id: string;
  type: 'direct' | 'group';
  name: string | null;
  avatarImageId: string | null;
  createdById: string;
  createdAt: Date;
  updatedAt: Date;
  participants: ConversationParticipant[];
  lastMessage?: Message;
  unreadCount?: number;
}

export interface ConversationParticipant {
  userId: string;
  role: string;
  joinedAt: Date;
  lastReadAt: Date | null;
  user: {
    id: string;
    username: string;
    fullName: string | null;
    avatarImageId: string | null;
  };
}

/**
 * Attachment metadata the hub hangs on an image/file message — the three fields
 * the chat surfaces read.
 */
export interface MessageMetadata {
  storageKey?: string;
  fileName?: string;
  fileSize?: number;
  [key: string]: unknown;
}

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  content: string | null;
  type: 'text' | 'image' | 'file' | 'system';
  metadata?: MessageMetadata;
  replyToId: string | null;
  editedAt: Date | null;
  deletedAt: Date | null;
  createdAt: Date;
  sender: {
    id: string;
    username: string;
    fullName: string | null;
    avatarImageId: string | null;
  };
  replyTo?: {
    id: string;
    content: string | null;
    type: string;
    senderId: string;
  } | null;
}

export const usePeopleStore = defineStore('people', () => {
  const socketStore = useSocketStore();
  const authStore = useAuthStore();

  // State
  const people = ref<User[]>([]);
  const conversations = ref<Conversation[]>([]);
  const activeConversationId = ref<string | null>(null);
  const messages = ref<Map<string, Message[]>>(new Map());
  const typingUsers = ref<Map<string, Set<string>>>(new Map());
  const loading = ref(false);
  const error = ref<string | null>(null);
  const myStatus = ref<'online' | 'away' | 'dnd' | 'offline'>('online');

  // Computed
  const activeConversation = computed(() => {
    if (!activeConversationId.value) return null;
    return conversations.value.find((c) => c.id === activeConversationId.value) || null;
  });

  const activeMessages = computed(() => {
    if (!activeConversationId.value) return [];
    return messages.value.get(activeConversationId.value) || [];
  });

  const unreadTotal = computed(() => {
    return conversations.value.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);
  });

  const typingInActive = computed(() => {
    if (!activeConversationId.value) return [];
    const typingSet = typingUsers.value.get(activeConversationId.value);
    if (!typingSet) return [];

    const currentUserId = authStore.user?.id;
    return Array.from(typingSet).filter((userId) => userId !== currentUserId);
  });

  // Actions
  /**
   * The app's PEOPLE list. Everything that paints people renders this one array —
   * the chat roster (`views/People.vue`, nav "Chat") and the chat pickers
   * (`ChatPanel.vue`, `UserDrawer.vue`) — so the directory rule is applied here,
   * where the list is built, and no surface classifies anything itself (card
   * `t_2f29f219`). (The launcher's "Who's around" panel was the third reader;
   * it left the home page with plan `HOME-ONLY-SERVER-CARDS.md` §3 A2.)
   *
   * `/api/people` is the hub's `getAllUsers()` + presence
   * (`otto/src/server/routes/people.routes.ts`), i.e. the SAME rows `/users`
   * serves, machine rows included: the two retired adoption anchors that paint as
   * "Windows PC" and "Owner", the disabled machine/test rows and the fixture
   * addresses. A machine is a device, never a person (`utils/userDirectory.ts`,
   * card `t_7fd5e611`), so `isDeviceRow` keeps them out of every people surface —
   * they are listed where they belong, Settings → Users → Devices → Machine
   * accounts.
   *
   * The hub's own `system` row (`agent | Otto`) never arrives here: `getAllUsers()`
   * filters `role: 'system'` out before the route runs (measured on the Dev hub
   * 2026-09-24: 11 rows, no Otto), and the hub keeps agent conversations out of
   * `/people/conversations` on purpose (`ConversationRepository.getConversationsForUser`
   * asks for `type in ('direct','group')`). So this roster is people-only by the
   * hub's own rule, and the filter keeps it that way if a `system` row ever
   * arrives. Otto is not lost by that: it is a conversation participant, reached
   * from the app's own agent surface (`/agent` → `views/Agent.vue`, the account
   * menu's `smart_toy` entry and the agent button), which talks to
   * `/api/agent/conversations`.
   */
  async function fetchPeople() {
    try {
      loading.value = true;
      error.value = null;
      const response = await apiFetch('/people');
      const rows: User[] = Array.isArray(response) ? response : [];
      people.value = rows.filter((row) => !isDeviceRow(row));
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message || 'Failed to fetch people';
      console.error('Error fetching people:', err);
    } finally {
      loading.value = false;
    }
  }

  async function fetchConversations() {
    try {
      loading.value = true;
      error.value = null;
      const response = await apiFetch('/people/conversations');
      conversations.value = response;
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message || 'Failed to fetch conversations';
      console.error('Error fetching conversations:', err);
    } finally {
      loading.value = false;
    }
  }

  async function fetchMessages(conversationId: string, cursor?: string): Promise<Message[]> {
    try {
      const url = cursor
        ? `/people/conversations/${conversationId}/messages?cursor=${cursor}&limit=50`
        : `/people/conversations/${conversationId}/messages?limit=50`;

      const response = await apiFetch(url);

      // Messages come in desc order (newest first), reverse for display
      const fetchedMessages = response.reverse();

      if (cursor) {
        // Pagination: prepend older messages to existing
        const existing = messages.value.get(conversationId) || [];
        messages.value.set(conversationId, [...fetchedMessages, ...existing]);
      } else {
        // Initial load: replace entirely to avoid duplicating socket-delivered messages
        messages.value.set(conversationId, fetchedMessages);
      }

      return fetchedMessages;
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message || 'Failed to fetch messages';
      console.error('Error fetching messages:', err);
      return [];
    }
  }

  /**
   * The hub's conversation-create body: a direct conversation names its
   * participants (`participantIds`, or a single `participantId`), a group one
   * also carries its name.
   */
  interface CreateConversationInput {
    participantIds?: string[];
    participantId?: string;
    name?: string;
    [key: string]: unknown;
  }

  async function createConversation(
    type: 'direct' | 'group',
    data: CreateConversationInput,
  ): Promise<Conversation | null> {
    try {
      const response = await apiFetch('/people/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, ...data }),
      });

      // Only add to list if not already present (backend returns existing DM if one exists)
      if (!conversations.value.find((c) => c.id === response.id)) {
        conversations.value.unshift(response);
      }
      return response;
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message || 'Failed to create conversation';
      console.error('Error creating conversation:', err);
      return null;
    }
  }

  async function sendMessage(conversationId: string, content: string, type: string = 'text', replyToId?: string) {
    try {
      socketStore.socket?.emit('people:chat:message', {
        conversationId,
        content,
        type,
        replyToId,
      });

      // Optimistically update UI could be added here
    } catch (err) {
      console.error('Error sending message:', err);
    }
  }

  async function markAsRead(conversationId: string) {
    try {
      socketStore.socket?.emit('people:chat:read', { conversationId });

      // Update local state
      const conversation = conversations.value.find((c) => c.id === conversationId);
      if (conversation) {
        conversation.unreadCount = 0;
      }
    } catch (err) {
      console.error('Error marking conversation as read:', err);
    }
  }

  function startTyping(conversationId: string) {
    socketStore.socket?.emit('people:chat:typing', {
      conversationId,
      isTyping: true,
    });
  }

  function stopTyping(conversationId: string) {
    socketStore.socket?.emit('people:chat:typing', {
      conversationId,
      isTyping: false,
    });
  }

  async function setMyStatus(status: 'online' | 'away' | 'dnd' | 'offline', customMessage?: string) {
    try {
      myStatus.value = status;
      socketStore.socket?.emit('people:status:set', { status, customMessage });
    } catch (err) {
      console.error('Error setting status:', err);
    }
  }

  function joinConversation(conversationId: string) {
    socketStore.socket?.emit('people:chat:join', { conversationId });
    activeConversationId.value = conversationId;

    // Mark as read when joining
    markAsRead(conversationId);
  }

  function leaveConversation(conversationId: string) {
    socketStore.socket?.emit('people:chat:leave', { conversationId });
  }

  async function clearMessages(conversationId: string): Promise<boolean> {
    try {
      await apiFetch(`/people/conversations/${conversationId}/messages`, {
        method: 'DELETE',
      });
      // Optimistic local clear (socket event will also fire for all clients)
      messages.value.set(conversationId, []);
      const conv = conversations.value.find((c) => c.id === conversationId);
      if (conv) {
        conv.lastMessage = undefined;
        conv.unreadCount = 0;
      }
      return true;
    } catch (err) {
      console.error('Error clearing messages:', err);
      return false;
    }
  }

  // -------------------------------------------------------
  // Socket event handlers
  // -------------------------------------------------------

  // Keep named handler references so we can remove them in cleanup()
  const handlers: Record<string, SocketHandler> = {};
  let listenersInitialized = false;

  function setupSocketListeners() {
    if (listenersInitialized) return;
    listenersInitialized = true;

    // Message received
    handlers['people:message'] = (message: Message) => {
      const convMessages = messages.value.get(message.conversationId) || [];
      convMessages.push(message);
      messages.value.set(message.conversationId, convMessages);

      const conv = conversations.value.find((c) => c.id === message.conversationId);
      if (conv) {
        conv.lastMessage = message;
        conv.updatedAt = message.createdAt;
        if (activeConversationId.value !== message.conversationId && message.senderId !== authStore.user?.id) {
          conv.unreadCount = (conv.unreadCount || 0) + 1;
        }
        conversations.value = conversations.value.sort(
          (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        );
      }
    };
    socketStore.on('people:message', handlers['people:message']);

    // Typing indicator
    handlers['people:typing'] = (data: { conversationId: string; userId: string; isTyping: boolean }) => {
      if (!typingUsers.value.has(data.conversationId)) {
        typingUsers.value.set(data.conversationId, new Set());
      }
      const typingSet = typingUsers.value.get(data.conversationId)!;
      if (data.isTyping) {
        typingSet.add(data.userId);
      } else {
        typingSet.delete(data.userId);
      }
    };
    socketStore.on('people:typing', handlers['people:typing']);

    // Read receipt
    handlers['people:read'] = (data: { conversationId: string; userId: string; timestamp: Date }) => {
      const conv = conversations.value.find((c) => c.id === data.conversationId);
      if (conv) {
        const participant = conv.participants.find((p) => p.userId === data.userId);
        if (participant) {
          participant.lastReadAt = data.timestamp;
        }
      }
    };
    socketStore.on('people:read', handlers['people:read']);

    // Status update
    handlers['people:status'] = (data: {
      userId: string;
      status: string;
      customMessage?: string;
      lastActiveAt: Date;
    }) => {
      const person = people.value.find((p) => p.userId === data.userId);
      if (person) {
        person.status = data.status;
        person.customMessage = data.customMessage || null;
        person.lastActiveAt = data.lastActiveAt;
      }
      // The hub broadcasts our own status too when it is changed from another
      // client/session. Keep the local "myStatus" ref (used by the user drawer
      // and header) in sync — without this the right menu stays stale while the
      // chats page (which reads the people array) updates correctly.
      if (data.userId && data.userId === authStore.user?.id) {
        const valid = ['online', 'away', 'dnd', 'offline'].includes(data.status);
        if (valid) {
          myStatus.value = data.status as 'online' | 'away' | 'dnd' | 'offline';
        }
      }
    };
    socketStore.on('people:status', handlers['people:status']);

    // Message edited
    handlers['people:message:edited'] = (message: Message) => {
      const convMessages = messages.value.get(message.conversationId);
      if (convMessages) {
        const index = convMessages.findIndex((m) => m.id === message.id);
        if (index !== -1) {
          convMessages[index] = message;
        }
      }
    };
    socketStore.on('people:message:edited', handlers['people:message:edited']);

    // Message deleted
    handlers['people:message:deleted'] = (data: { messageId: string; conversationId: string }) => {
      const convMessages = messages.value.get(data.conversationId);
      if (convMessages) {
        const index = convMessages.findIndex((m) => m.id === data.messageId);
        if (index !== -1) {
          convMessages[index].deletedAt = new Date();
          convMessages[index].content = null;
        }
      }
    };
    socketStore.on('people:message:deleted', handlers['people:message:deleted']);

    // All messages cleared (clear chat)
    handlers['people:messages:cleared'] = (data: { conversationId: string }) => {
      messages.value.set(data.conversationId, []);
      // Clear last message preview in sidebar
      const conv = conversations.value.find((c) => c.id === data.conversationId);
      if (conv) {
        conv.lastMessage = undefined;
        conv.unreadCount = 0;
      }
    };
    socketStore.on('people:messages:cleared', handlers['people:messages:cleared']);
  }

  /**
   * Remove all socket listeners registered by this store.
   * Call from the component's onUnmounted to prevent duplicate messages on re-mount.
   */
  function cleanup() {
    for (const [event, handler] of Object.entries(handlers)) {
      socketStore.off(event, handler);
    }
    listenersInitialized = false;
  }

  function initialize() {
    setupSocketListeners();
  }

  return {
    // State
    people,
    conversations,
    activeConversationId,
    messages,
    typingUsers,
    loading,
    error,
    myStatus,

    // Computed
    activeConversation,
    activeMessages,
    unreadTotal,
    typingInActive,

    // Actions
    fetchPeople,
    fetchConversations,
    fetchMessages,
    createConversation,
    sendMessage,
    markAsRead,
    startTyping,
    stopTyping,
    setMyStatus,
    joinConversation,
    leaveConversation,
    clearMessages,
    initialize,
    cleanup,
  };
});
